<template>
  <div
    :id="`userAnalysis-${userAnalysis.id}`"
    style="margin-top: 1px"
    class="analysis-card-content"
  >
    <div
      class="d-flex align-center py-4 pl-4"
    >
      <Avatar
        :user-id="userAnalysis.id"
        ellipsis
        :size="44"
      />
      <div class="pa-0 ml-2">
        <div class="noWrap text-username">
          {{ getUserName(userAnalysis.id) }}
        </div>
        <div
          v-if="userAnalysis.auditInfo"
          class="noWrap avatarCaption"
        >
          {{ $t('extension.userAnalyses.events.modified') }}
          {{ formatTimestamp(userAnalysis.auditInfo.modifiedOn) }}
        </div>
        <div
          v-else
          class="noWrap avatarCaption"
        >
          {{ $t('extension.userAnalyses.description') }}
        </div>
      </div>
    </div>
    <div class="analysisGrid pb-2 flex-grow-1">
      <div class="sectionIcon material-icons-outline">
        <v-icon>diamond</v-icon>
      </div>
      <div class="d-flex align-center analysis-section-title" style="height: 55px">
        <div>
          {{ $t('extension.userAnalyses.evaluation') }}
        </div>
        <Rating
          class="pl-8"
          :read-only="!isAnalysisOwnedByCurrentUser"
          :score="userAnalysis.evaluation.documentScore"
          color="#3C44C8"
          @change="ratingChanged"
        />
      </div>
      <div class="sectionIcon">
        <v-icon>format_quote</v-icon>
      </div>
      <AnalysisElementSection
        type="highlight"
        :elements="userAnalysis.evaluation.significantElements"
        :user-analysis="userAnalysis"
        :analysis-id="analysisId"
        :read-only="!isAnalysisOwnedByCurrentUser"
        @addNewElement="addNewElement"
        @archiveElement="archiveElement"
        @archiveThreadComment="archiveThreadComment"
      />
      <div class="sectionIcon">
        <v-icon>chat_bubble_outline</v-icon>
      </div>
      <AnalysisElementSection
        type="comment"
        :elements="userAnalysis.reflection.comments"
        :user-analysis="userAnalysis"
        :analysis-id="analysisId"
        :read-only="!isAnalysisOwnedByCurrentUser"
        @addNewElement="addNewElement"
        @archiveElement="archiveElement"
        @archiveThreadComment="archiveThreadComment"
      />
      <div class="sectionIcon">
        <v-icon>adjust</v-icon>
      </div>
      <Missions
        :analysis-id="analysisId"
        :user-analysis="userAnalysis"
        :read-only="!isAnalysisOwnedByCurrentUser"
        @addNewElement="addNewElement"
      />
      <div style="height: 20px;" />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import AnalysisElementSection from './AnalysisElementSection';
import Rating from '../ExpressAnalysis/EvaluationForm/Rating';
import Avatar from '../avatar/Avatar';
import Missions from './mission/Missions';
import { types as analysesTypes } from '../../store/analyses';
import { types as userTypes } from '../../store/user';
import { types as usersTypes } from '../../store/users';
import { getHiglightedText, registerEventListener } from '../../utils/EventTransmitter';

export default {
  name: 'AnalysisCard',
  components: {
    Avatar,
    Rating,
    AnalysisElementSection,
    Missions,
  },
  props: {
    userAnalysis: {
      type: Object,
      required: true,
    },
    analysisId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      usersProfiles: usersTypes.getters.usersProfiles,
      userDetails: userTypes.getters.userDetails,
    }),
    isAnalysisOwnedByCurrentUser() {
      return this.userAnalysis.id === this.userDetails.userId;
    },
  },
  methods: {
    ...mapActions({
      getUserProfile: usersTypes.actions.getUserProfile,
      updateUserAnalysisVote: analysesTypes.actions.updateUserAnalysisVote,
      updateUserAnalysisRating: analysesTypes.actions.updateUserAnalysisRating,
      addUserAnalysisElement: analysesTypes.actions.addUserAnalysisElement,
      archiveUserAnalysisElement: analysesTypes.actions.archiveUserAnalysisElement,
      saveThreadComment: analysesTypes.actions.saveThreadComment,
    }),
    formatTimestamp(timestamp) {
      return timestamp ? moment(timestamp).fromNow() : '';
    },
    captureSignificantElement() {
      getHiglightedText();
    },
    addSignificantElement(text) {
      console.log('addSignificantElement from ACO', text, 'with', this.userAnalysis, 'and', this.analysisId);
      this.addUserAnalysisElement({
        element: {
          type: 'highlight',
          text,
        },
        analysisId: this.analysisId,
        userAnalysis: this.userAnalysis,
      });
    },
    archiveElement(element) {
      this.archiveUserAnalysisElement({
        userAnalysis: this.userAnalysis,
        element,
      });
    },
    archiveThreadComment(commentId) {
      this.saveThreadComment({
        analysisId: this.analysisId,
        comment: {
          id: commentId,
          archived: true,
        },
      });
    },
    addNewElement({ text, type, codeClassification }) {
      console.log('add new element to ', this.analysisId, 'before the if in addNewElement in AnalysisCardOld');
      if (type === 'mission') {
        console.log('add new mission to ', this.analysisId);
        return;
      }
      if (type === 'highlight') {
        this.captureSignificantElement();
        return;
      }
      console.log('addNewElement', this.analysisId);

      this.addUserAnalysisElement({
        element: {
          type,
          text,
          codeClassification,
        },
        analysisId: this.analysisId,
        userAnalysis: this.userAnalysis,
      });
    },
    getUserName(userId) {
      this.getUserProfile(userId);
      const userProfile = this.usersProfiles[userId];
      return userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : '';
    },
    ratingChanged(rating) {
      this.updateUserAnalysisRating({
        userAnalysis: this.userAnalysis,
        rating,
      });
    },
  },
  created() {
    console.log('******** AnalysisCardOld created! UserAnalysis: ', this.userAnalysis);
    if (this.isAnalysisOwnedByCurrentUser) {
      registerEventListener(
        'SignificantElements',
        text => this.addSignificantElement(text),
      );
    }
  },
};
</script>

<style scoped>
.card {
  min-width: 484px;
  max-width: 1000px;
  min-height: 66px;
  border: 28px;
}
.expansionPanelHeader {
  padding: 0;
  font-size: 13px;
}

.elementHeaderText {
  font-size: 13px;
  /* font-style: italic; */
  color: #848d95;
}

.myAnalysis {
  font-size: 13px;
  font-style: italic;
  color: #848d95;
}
.fiveGemsScore {
  display:flex;
  margin-left: -4px;
}

.votesIcons {
  width: 16px;
  height: 16px;
  color: default;
}

.votesIcons:hover {
  color: #06D6A0;
}

.votesIcons:focus::after {
  opacity: 0;
}
.noWrap {
  white-space: nowrap;
}

.upVoteSet {
  color: #06D6A0;
}

.votes {
  min-width: 45px;
  max-width: 45px;
  height: 66px;
}

.votesCounters {
  font-size: 11px;
  /* width: 30px;*/
  min-width: 30px;
  max-width: 30px;
  text-align: end;
  position: relative;
}

.votesCountersInside {
  position: absolute;
  top: 3px;
  left: -2px;
  width: 30px;
}

.votesCountersInsideAnalysis {
  position: absolute;
  left: 0px;
  width: 30px
}
.buttons {
  font-size: 13px;
  font-weight: 500;
}
.text-username {
  font-size: 14px;
  font-weight: 600;
}
.analysis-card-content {
  background-color: white;
  flex-direction: column;
}

.avatarCaption {
  font-size: 11px;
  color: #848d95;
}

::v-deep .analysis-section-title {
  font-size: 13px;
  font-weight: 500;
  height: 55px;
}

.analysisGrid {
  display: grid;
  grid-template-columns: 55px auto;
}

.sectionIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 55px;
}

.greyBackground {
  /*background-color: #c2c2c2;*/
  /*background-color: #f6f7ff;*/
  background-color: #cbcdf0;
  /*background-color: red;*/

}

.whiteBackground {
  transition: background-color 1.5s ease;
  background-color: white;
}

</style>

<style>
.v-expansion-panel-content__wrap {
  padding-bottom: 0;
}
</style>
